/**
 * PeopleMatters SSO Session Manager
 * 
 * A comprehensive JavaScript session management system for PeopleMatters SSO integration
 * with cookie-based storage, automatic session renewal, and multi-device support.
 * 
 * Features:
 * - 🔐 SSO Integration: Seamless integration with PeopleMatters SSO
 * - ⏰ 1-Hour Session Expiry: Automatic session expiration and renewal
 * - 🍪 Cookie-Based Storage: Secure session data storage using HTTP cookies
 * - 🖥️ Multi-Device Support: Handle sessions across multiple devices with user ID priority
 * - 💾 Data Persistence: Save session data before expiry and restore on new sessions
 * - 🔄 Auto-Renewal: Automatic session extension every 5 minutes
 * - 🛡️ Session Validation: Continuous session validity checking
 */

import { userTrackingService } from '@/services/userTrackingService';
import { matchSiteConfig } from './getApiBaseUrl';
import { SessionUser } from '@/types/common';

// Types
export interface SSOSessionData {
  accessToken: string;
  refreshToken?: string;
  user_id: string | number; // Unique UUID for the user (permanent identifier)
  subscriber_id: number; // Login ID (can change, used for authentication)
  user_email: string;
  user_mobile?: string;
  first_name: string;
  last_name?: string;
  company_name?: string;
  designation?: string;
  country_name?: string;
  is_subscribe_news_letter?: boolean;
  is_accept_terms?: boolean;
  is_user_type?: string;
  userData?: Record<string, unknown>;
  sessionId: string;
  deviceId: string;
  domain: string;
  siteType: string;
  expiresAt: number;
  lastRenewed: number;
  isActive: boolean;
}

export interface SessionConfig {
  sessionDuration: number; // in milliseconds (default: 1 hour)
  renewalInterval: number; // in milliseconds (default: 5 minutes)
  maxDevices: number; // maximum devices per user (default: 5)
  cookieName: string;
  cookieOptions: {
    secure: boolean;
    httpOnly: boolean;
    sameSite: 'strict' | 'lax' | 'none';
    path: string;
    domain?: string;
  };
}

export interface DeviceInfo {
  deviceId: string;
  userAgent: string;
  lastActive: number;
  isActive: boolean;
  domain: string;
  user_id: string | number; // Unique UUID for the user
  subscriber_id: number; // Login ID for the user
}

export interface SessionEvent {
  type: 'session_created' | 'session_renewed' | 'session_expired' | 'session_terminated' | 'device_added' | 'device_removed' | 'device_detected';
  sessionId: string;
  deviceId: string;
  user_id: string | number; // Unique UUID for the user
  subscriber_id: number; // Login ID for the user
  timestamp: number;
  data?: Record<string, unknown>;
}

interface PersistedSessionData {
  subscriber_id: number;
  user_id?: string | number; // Add missing user_id field
  user_email: string;
  user_mobile?: string;
  first_name: string;
  last_name?: string;
  company_name?: string;
  designation?: string;
  country_name?: string;
  is_subscribe_news_letter?: boolean;
  is_accept_terms?: boolean;
  is_user_type?: string;
  userData?: Record<string, unknown>;
  lastPageVisitData?: Record<string, unknown>;
  lastUserInteractionData?: Record<string, unknown>;
  sessionId?: string;
  deviceId?: string;
  domain?: string;
  siteType?: string;
  accessToken?: string;
  lastActive?: number;
}

interface DecodedJWT {
  user_id?: number;
  uuid?: string;
  user_uuid?: string;
  subscriber_id?: number;
  id?: number;
  email?: string;
  user_email?: string;
  mobile?: string;
  phone?: string;
  first_name?: string;
  given_name?: string;
  last_name?: string;
  family_name?: string;
  company_name?: string;
  company?: string;
  designation?: string;
  title?: string;
  country_name?: string;
  country?: string;
  is_subscribe_news_letter?: boolean;
  is_accept_terms?: boolean;
  is_user_type?: string;
  exp?: number;
  iat?: number;
}

// Default configuration
const DEFAULT_CONFIG: SessionConfig = {
  sessionDuration: 2 * 24 * 60 * 60 * 1000, // 2 days
  renewalInterval: 2 * 24 * 60 * 60 * 1000, // 2 days (same as session duration)
  maxDevices: 5,
  cookieName: 'pm_sso_session',
  cookieOptions: {
    secure: process.env.NODE_ENV === 'production',
    httpOnly: false, // Set to true for server-side only access
    sameSite: 'lax',
    path: '/',
  }
};

class PeopleMattersSSOSessionManager {
  private config: SessionConfig;
  private currentSession: SSOSessionData | null = null;
  private renewalTimer: NodeJS.Timeout | null = null;
  private validationTimer: NodeJS.Timeout | null = null;
  private eventListeners: Map<string, Function[]> = new Map();
  private deviceRegistry: Map<number, DeviceInfo[]> = new Map();
  private sessionStorage: Map<string, SSOSessionData> = new Map();

  constructor(config: Partial<SessionConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.initializeSessionManager();
  }

  /**
   * Initialize the session manager with SSO integration
   */
  private initializeSessionManager(): void {
    if (typeof window === 'undefined') return;

    // Check for SSO tokens first
    this.detectSSOTokens().then(ssoUserData => {
      if (ssoUserData) {
        // console.log('🔐 SSO tokens detected during initialization, creating session');
        this.createSession(ssoUserData.userData, ssoUserData.token).catch(error => {
          // console.error('Failed to create session during initialization:', error);
          // Fall back to loading existing session from cookies
          this.loadSessionFromCookies();
        });
      } else {
        // console.log('🔐 No SSO tokens found during initialization, loading existing session');
        // Load existing session from cookies
        this.loadSessionFromCookies();
      }
    }).catch(error => {
      console.error('SSO token detection failed during initialization:', error);
      // Fall back to loading existing session from cookies
      this.loadSessionFromCookies();
    });
    
    // Set up automatic renewal
    this.startAutoRenewal();
    
    // Set up session validation
    this.startSessionValidation();
    
    // Handle page visibility changes
    this.setupVisibilityHandlers();
    
    // Handle beforeunload for data persistence
    this.setupBeforeUnloadHandler();
  }

  /**
   * Create a new SSO session with UUID and user ID
   */
  public async createSession(userData: SessionUser, token: string): Promise<SSOSessionData> {
    const deviceId = this.generateDeviceId();
    const sessionId = this.generateSessionId();
    const domain = window.location.hostname;
    const siteConfig = matchSiteConfig(domain);
    
    // Save current session data before creating new one (if exists)
    if (this.currentSession && this.isSessionValid()) {
      await this.saveDataBeforeExpiry();
    }
    
    // Generate or retrieve user_id (unique UUID for the user)
    let user_id = userData.user_id;
    if (!user_id) {
      // Check if we have a stored user_id for this subscriber_id
      user_id = this.getStoredUserIdForSubscriber(userData.subscriber_id || 0) || undefined;
      if (!user_id) {
        // Generate new user_id for this subscriber
        user_id = this.generateUserId();
        this.storeUserIdForSubscriber(userData.subscriber_id || 0, user_id);
      }
    }
    
    const sessionData: SSOSessionData = {
      accessToken: token,
      user_id: user_id, // Unique UUID for the user
      subscriber_id: userData.subscriber_id || 0, // Login ID
      user_email: userData.user_email || '',
      user_mobile: userData.user_mobile,
      first_name: userData.first_name || '',
      last_name: userData.last_name,
      company_name: userData.company_name,
      designation: userData.designation,
      country_name: userData.country_name,
      is_subscribe_news_letter: userData.is_subscribe_news_letter,
      is_accept_terms: userData.is_accept_terms,
      is_user_type: userData.is_user_type,
      userData: userData.userData,
      sessionId,
      deviceId,
      domain,
      siteType: siteConfig.siteType,
      expiresAt: Date.now() + this.config.sessionDuration,
      lastRenewed: Date.now(),
      isActive: true,
    };

    // Handle multi-device support
    await this.handleMultiDeviceSession(sessionData);

    // Store session
    this.currentSession = sessionData;
    this.sessionStorage.set(sessionId, sessionData);
    
    // Save to cookies
    this.saveSessionToCookies(sessionData);
    
    // Update device registry
    this.updateDeviceRegistry(sessionData);
    
    // Emit session created event
    this.emitEvent({
      type: 'session_created',
      sessionId,
      deviceId,
      user_id: sessionData.user_id,
      subscriber_id: sessionData.subscriber_id,
      timestamp: Date.now(),
      data: { domain, siteType: siteConfig.siteType }
    });

    // console.log(`🆕 New session created: ${sessionId} for user ${sessionData.subscriber_id}, expires in 2 days`);

    return sessionData;
  }

  /**
   * Get current session data
   */
  public getCurrentSession(): SSOSessionData | null {
    return this.currentSession;
  }

  /**
   * Check if session is valid and not expired
   */
  public isSessionValid(): boolean {
    if (!this.currentSession) return false;
    
    const now = Date.now();
    const isNotExpired = this.currentSession.expiresAt > now;
    const isActive = this.currentSession.isActive;
    const hasValidUser = !!this.currentSession.user_id && this.currentSession.subscriber_id > 0;
    
    return isNotExpired && isActive && hasValidUser;
  }

  /**
   * Check if user is logged in (has valid token or session)
   */
  public isUserLoggedIn(): boolean {
    // Check if we have a valid session
    if (this.isSessionValid()) {
      return true;
    }
    
    // Check if we have a valid token in localStorage
    const token = localStorage.getItem('peoples_token');
    if (token && token.trim() !== '') {
      return true;
    }
    
    // Check if we have a valid token in cookies
    const cookieToken = this.getCookieValue('peoples_token');
    if (cookieToken && cookieToken.trim() !== '') {
      return true;
    }
    
    return false;
  }

  /**
   * Manually check and restore session if user is logged in
   */
  public async checkAndRestoreSession(): Promise<boolean> {
    try {
      // If we already have a valid session, no need to restore
      if (this.isSessionValid()) {
        // console.log('✅ Session is already valid, no restoration needed');
        return true;
      }

      // Check if user is logged in
      if (!this.isUserLoggedIn()) {
        // console.log('❌ User is not logged in, cannot restore session');
        return false;
      }

      // Try to detect SSO tokens and create new session
      const ssoUserData = await this.detectSSOTokens();
      if (ssoUserData) {
        // console.log('🔐 Attempting to restore session from SSO tokens');
        await this.createSession(ssoUserData.userData, ssoUserData.token);
        // console.log('✅ Session restored successfully');
        return true;
      }

      // console.log('❌ No valid SSO tokens found for session restoration');
      return false;
    } catch (error) {
      console.error('Failed to restore session:', error);
      return false;
    }
  }

  /**
   * Renew session (extend expiry time by 2 days if valid)
   */
  public async renewSession(): Promise<boolean> {
    if (!this.currentSession || !this.isSessionValid()) {
      return false;
    }

    try {
      // Validate session with server
      const isValid = await this.validateSessionWithServer();
      if (!isValid) {
        // console.log('🔄 Session validation failed, creating new session');
        await this.createNewSessionAfterExpiry();
        return false;
      }

      // Save data before extending session
      await this.saveDataBeforeExpiry();

      // Extend session by 2 days
      this.currentSession.expiresAt = Date.now() + this.config.sessionDuration;
      this.currentSession.lastRenewed = Date.now();
      
      // Update storage
      this.sessionStorage.set(this.currentSession.sessionId, this.currentSession);
      this.saveSessionToCookies(this.currentSession);
      
      // Emit renewal event
      this.emitEvent({
        type: 'session_renewed',
        sessionId: this.currentSession.sessionId,
        deviceId: this.currentSession.deviceId,
        user_id: this.currentSession.user_id,
        subscriber_id: this.currentSession.subscriber_id,
        timestamp: Date.now()
      });

      // console.log(`🔄 Session renewed: ${this.currentSession.sessionId}, extended by 2 days`);

      return true;
    } catch (error) {
      console.error('Session renewal failed:', error);
      await this.createNewSessionAfterExpiry();
      return false;
    }
  }

  /**
   * Terminate current session
   */
  public async terminateSession(): Promise<void> {
    if (!this.currentSession) return;

    const sessionId = this.currentSession.sessionId;
    const deviceId = this.currentSession.deviceId;
    // const userId = this.currentSession.subscriber_id;

    // Mark session as inactive
    this.currentSession.isActive = false;
    
    // Remove from storage
    this.sessionStorage.delete(sessionId);
    
    // Clear cookies
    this.clearSessionCookies();
    
    // Remove device from registry
    this.removeDeviceFromRegistry(this.currentSession.subscriber_id, deviceId);
    
    // Clear timers
    this.clearTimers();
    
    // Emit termination event
    this.emitEvent({
      type: 'session_terminated',
      sessionId,
      deviceId,
      user_id: this.currentSession.user_id,
      subscriber_id: this.currentSession.subscriber_id,
      timestamp: Date.now()
    });

    this.currentSession = null;
  }

  /**
   * Save data before session expiry
   */
  public async saveDataBeforeExpiry(): Promise<void> {
    if (!this.currentSession) return;

    const sessionData = this.getSessionDataForPersistence();
    if (sessionData) {
      // Save to localStorage for persistence
      localStorage.setItem('pm_session_backup', JSON.stringify({
        ...sessionData,
        sessionId: this.currentSession.sessionId,
        deviceId: this.currentSession.deviceId,
        accessToken: this.currentSession.accessToken,
        savedAt: Date.now(),
        expiresAt: this.currentSession.expiresAt,
      }));
      
      // console.log(`💾 Session data saved before expiry: ${this.currentSession.sessionId}`);
    }
  }

  /**
   * Get session data for persistence before expiry
   */
  public getSessionDataForPersistence(): PersistedSessionData | null {
    if (!this.currentSession) return null;

    return {
      subscriber_id: this.currentSession.subscriber_id,
      user_email: this.currentSession.user_email,
      first_name: this.currentSession.first_name,
      last_name: this.currentSession.last_name,
      company_name: this.currentSession.company_name,
      designation: this.currentSession.designation,
      country_name: this.currentSession.country_name,
      is_subscribe_news_letter: this.currentSession.is_subscribe_news_letter,
      is_accept_terms: this.currentSession.is_accept_terms,
      is_user_type: this.currentSession.is_user_type,
      userData: this.currentSession.userData,
      lastActive: this.currentSession.lastRenewed,
      domain: this.currentSession.domain,
      siteType: this.currentSession.siteType,
    };
  }

  /**
   * Create new session when old one expires
   */
  public async createNewSessionAfterExpiry(): Promise<SSOSessionData | null> {
    try {
      // Try to restore from backup data
      const backupData = localStorage.getItem('pm_session_backup');
      if (backupData) {
        const parsedData = JSON.parse(backupData);
        const token = parsedData.accessToken || localStorage.getItem('peoples_token');
        
        if (token && parsedData.subscriber_id) {
          // console.log('🔄 Creating new session from backup data after expiry');
          return await this.restoreSession(parsedData, token);
        }
      }
      
      // console.log('🔄 No backup data found, user needs to login again');
      return null;
    } catch (error) {
      console.error('Failed to create new session after expiry:', error);
      return null;
    }
  }

  /**
   * Restore session from persisted data
   */
  public async restoreSession(persistedData: PersistedSessionData, token: string): Promise<SSOSessionData | null> {
    try {
      const userData: SessionUser = {
        accessToken: token,
        subscriber_id: persistedData.subscriber_id,
        user_email: persistedData.user_email,
        user_mobile: persistedData.user_mobile,
        first_name: persistedData.first_name,
        last_name: persistedData.last_name,
        company_name: persistedData.company_name,
        designation: persistedData.designation,
        country_name: persistedData.country_name,
        is_subscribe_news_letter: persistedData.is_subscribe_news_letter,
        is_accept_terms: persistedData.is_accept_terms,
        is_user_type: persistedData.is_user_type,
        userData: persistedData.userData,
        token,
      };

      return await this.createSession(userData, token);
    } catch (error) {
      console.error('Session restoration failed:', error);
      return null;
    }
  }

  /**
   * Get active devices for current user
   */
  public getActiveDevices(): DeviceInfo[] {
    if (!this.currentSession) return [];
    
    const devices = this.deviceRegistry.get(this.currentSession.subscriber_id) || [];
    return devices.filter(device => device.isActive);
  }

  /**
   * Remove a specific device
   */
  public async removeDevice(deviceId: string): Promise<boolean> {
    if (!this.currentSession) return false;

    const user_id = this.currentSession.user_id;
    const devices = this.deviceRegistry.get(this.currentSession.subscriber_id) || [];
    const deviceIndex = devices.findIndex(device => device.deviceId === deviceId);
    
    if (deviceIndex === -1) return false;

    // If removing current device, terminate session
    if (deviceId === this.currentSession.deviceId) {
      await this.terminateSession();
      return true;
    }

    // Mark device as inactive
    devices[deviceIndex].isActive = false;
    this.deviceRegistry.set(this.currentSession.subscriber_id, devices);

    // Emit device removed event
    this.emitEvent({
      type: 'device_removed',
      sessionId: this.currentSession.sessionId,
      deviceId,
      user_id: user_id,
      subscriber_id: this.currentSession.subscriber_id,
      timestamp: Date.now()
    });

    return true;
  }

  /**
   * Add event listener
   */
  public addEventListener(eventType: string, callback: Function): void {
    if (!this.eventListeners.has(eventType)) {
      this.eventListeners.set(eventType, []);
    }
    this.eventListeners.get(eventType)!.push(callback);
  }

  /**
   * Remove event listener
   */
  public removeEventListener(eventType: string, callback: Function): void {
    const listeners = this.eventListeners.get(eventType);
    if (listeners) {
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  // Private methods

  private generateSessionId(): string {
    return `pm_session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private generateUserId(): string {
    // Generate a unique UUID for the user
    return `pm_user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private getStoredUserIdForSubscriber(subscriber_id: number): string | null {
    try {
      const stored = localStorage.getItem(`pm_user_id_${subscriber_id}`);
      return stored || null;
    } catch (error) {
      console.error('Failed to get stored user_id for subscriber:', error);
      return null;
    }
  }

  private storeUserIdForSubscriber(subscriber_id: number, user_id: string): void {
    try {
      localStorage.setItem(`pm_user_id_${subscriber_id}`, user_id);
      // console.log(`🔗 Stored user_id ${user_id} for subscriber_id ${subscriber_id}`);
    } catch (error) {
      console.error('Failed to store user_id for subscriber:', error);
    }
  }

  private generateDeviceId(): string {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx?.fillText('Device fingerprint', 2, 2);
    const fingerprint = canvas.toDataURL();
    
    return `pm_device_${btoa(fingerprint + navigator.userAgent).substr(0, 16)}`;
  }

  private async handleMultiDeviceSession(sessionData: SSOSessionData): Promise<void> {
    const user_id = sessionData.user_id; // Use unique user_id for device management
    const subscriber_id = sessionData.subscriber_id; // Login ID
    const currentDeviceId = sessionData.deviceId;
    const devices = this.deviceRegistry.get(subscriber_id) || [];
    
    // Check if this is a new device for the user
    const existingDevice = devices.find(device => device.deviceId === currentDeviceId);
    const isNewDevice = !existingDevice;
    
    if (isNewDevice) {
      // console.log(`🆕 New device detected for user ${user_id} (subscriber: ${subscriber_id}): ${currentDeviceId}`);
      
      // Save current session data before switching (if exists and different user)
      if (this.currentSession && this.currentSession.user_id !== user_id) {
        await this.saveDataBeforeExpiry();
        // console.log(`💾 Saved session data before user switch from ${this.currentSession.user_id} to ${user_id}`);
      }
      
      // Add new device
      const newDevice: DeviceInfo = {
        deviceId: currentDeviceId,
        userAgent: navigator.userAgent,
        lastActive: Date.now(),
        isActive: true,
        domain: sessionData.domain,
        user_id: user_id,
        subscriber_id: subscriber_id,
      };
      
      devices.push(newDevice);
      
      // Emit device added event
      this.emitEvent({
        type: 'device_added',
        sessionId: sessionData.sessionId,
        deviceId: currentDeviceId,
        user_id: user_id,
        subscriber_id: subscriber_id,
        timestamp: Date.now(),
        data: { 
          userAgent: navigator.userAgent, 
          domain: sessionData.domain,
          isNewDevice: true,
          totalDevices: devices.length
        }
      });
    } else {
      // Update existing device
      existingDevice.lastActive = Date.now();
      existingDevice.isActive = true;
      existingDevice.domain = sessionData.domain;
      existingDevice.subscriber_id = subscriber_id; // Update subscriber_id in case it changed
      
      // console.log(`🔄 Existing device updated for user ${user_id} (subscriber: ${subscriber_id}): ${currentDeviceId}`);
    }
    
    // Enforce device limit with user ID prioritization
    if (devices.length > this.config.maxDevices) {
      // Remove oldest inactive devices first
      const inactiveDevices = devices.filter(device => !device.isActive);
      inactiveDevices.sort((a, b) => a.lastActive - b.lastActive);
      
      // Remove excess devices
      const devicesToRemove = devices.length - this.config.maxDevices;
      for (let i = 0; i < devicesToRemove && i < inactiveDevices.length; i++) {
        const deviceToRemove = inactiveDevices[i];
        const deviceIndex = devices.findIndex(device => device.deviceId === deviceToRemove.deviceId);
        if (deviceIndex > -1) {
          // console.log(`🗑️ Removing oldest device to make room: ${deviceToRemove.deviceId}`);
          devices.splice(deviceIndex, 1);
        }
      }
    }
    
    this.deviceRegistry.set(subscriber_id, devices);
    
    // Emit device detection event
    this.emitEvent({
      type: 'device_detected',
      sessionId: sessionData.sessionId,
      deviceId: currentDeviceId,
      user_id: user_id,
      subscriber_id: subscriber_id,
      timestamp: Date.now(),
      data: { 
        isNewDevice, 
        totalDevices: devices.length,
        domain: sessionData.domain,
        siteType: sessionData.siteType
      }
    });
  }

  private updateDeviceRegistry(sessionData: SSOSessionData): void {
    // const user_id = sessionData.user_id;
    const devices = this.deviceRegistry.get(sessionData.subscriber_id) || [];
    const device = devices.find(d => d.deviceId === sessionData.deviceId);
    
    if (device) {
      device.lastActive = Date.now();
      device.isActive = true;
      device.domain = sessionData.domain;
      device.subscriber_id = sessionData.subscriber_id; // Update subscriber_id
    }
    
    this.deviceRegistry.set(sessionData.subscriber_id, devices);
  }

  private removeDeviceFromRegistry(subscriber_id: number, deviceId: string): void {
    const devices = this.deviceRegistry.get(subscriber_id) || [];
    const deviceIndex = devices.findIndex(device => device.deviceId === deviceId);
    
    if (deviceIndex > -1) {
      devices[deviceIndex].isActive = false;
      this.deviceRegistry.set(subscriber_id, devices);
    }
  }

  private async validateSessionWithServer(): Promise<boolean> {
    if (!this.currentSession) return false;

    try {
      
      // Use the validateSession method from userTrackingService
      await userTrackingService.validateSession(
        this.currentSession.sessionId,
        this.currentSession.deviceId,
      );
      
      // console.log('🔐 Session validation sent via userTrackingService');
      
      // Return true for now - the actual validation response would need to be handled by the server
      return true;
    } catch (error) {
      console.error('Session validation failed:', error);
      return false;
    }
  }

  private startAutoRenewal(): void {
    this.renewalTimer = setInterval(async () => {
      if (this.isSessionValid()) {
        // console.log('🔄 Auto-renewal triggered: extending session by 2 days');
        await this.renewSession();
      } else {
        // console.log('🔄 Session expired, creating new session');
        await this.createNewSessionAfterExpiry();
      }
    }, this.config.renewalInterval);
  }

  private startSessionValidation(): void {
    this.validationTimer = setInterval(async () => {
      if (!this.isSessionValid()) {
        // console.log('⏰ Session validation: Current session is invalid');
        
        // Check if user is still logged in (has valid tokens)
        if (this.isUserLoggedIn()) {
          // console.log('🔐 User still has valid tokens, attempting to restore session');
          
          // Try to detect SSO tokens and create new session
          const ssoUserData = await this.detectSSOTokens();
          if (ssoUserData) {
            // console.log('🔐 SSO tokens found during validation, creating new session');
            try {
              await this.createSession(ssoUserData.userData, ssoUserData.token);
              // console.log('✅ Session restored successfully from SSO tokens');
              return; // Successfully created new session, no need to emit expired event
            } catch (error) {
              console.error('Failed to create session from SSO tokens:', error);
            }
          }
        }
        
        // Disabled automatic session expiry events to prevent redirects
        // Only log the status without emitting events that could trigger redirects
        // if (!this.isUserLoggedIn()) {
        //   console.log('⏰ User not logged in, session expired (no automatic redirect)');
        // } else {
        //   console.log('⏰ Session invalid but user still has tokens, will retry next validation');
        // }
      }
    }, 60000); // Check every minute
  }

  private clearTimers(): void {
    if (this.renewalTimer) {
      clearInterval(this.renewalTimer);
      this.renewalTimer = null;
    }
    if (this.validationTimer) {
      clearInterval(this.validationTimer);
      this.validationTimer = null;
    }
  }

  private setupVisibilityHandlers(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.currentSession) {
        // Validate session when page becomes visible
        this.validateSessionWithServer();
      }
    });
  }

  private setupBeforeUnloadHandler(): void {
    window.addEventListener('beforeunload', async () => {
      if (this.currentSession && this.isSessionValid()) {
        // Save session data for persistence before page unload
        await this.saveDataBeforeExpiry();
      }
    });
  }

  private saveSessionToCookies(sessionData: SSOSessionData): void {
    const cookieValue = btoa(JSON.stringify(sessionData));
    const expires = new Date(sessionData.expiresAt).toUTCString();
    
    let cookieString = `${this.config.cookieName}=${cookieValue}; expires=${expires}; path=${this.config.cookieOptions.path}`;
    
    if (this.config.cookieOptions.secure) {
      cookieString += '; secure';
    }
    
    if (this.config.cookieOptions.sameSite) {
      cookieString += `; samesite=${this.config.cookieOptions.sameSite}`;
    }
    
    if (this.config.cookieOptions.domain) {
      cookieString += `; domain=${this.config.cookieOptions.domain}`;
    }
    
    document.cookie = cookieString;
  }

  private loadSessionFromCookies(): void {
    const cookies = document.cookie.split(';');
    const sessionCookie = cookies.find(cookie => 
      cookie.trim().startsWith(`${this.config.cookieName}=`)
    );
    
    if (sessionCookie) {
      try {
        const cookieValue = sessionCookie.split('=')[1];
        const sessionData: SSOSessionData = JSON.parse(atob(cookieValue));
        
        // Check if session is still valid
        if (sessionData.expiresAt > Date.now() && sessionData.isActive) {
          this.currentSession = sessionData;
          this.sessionStorage.set(sessionData.sessionId, sessionData);
        } else {
          this.clearSessionCookies();
        }
      } catch (error) {
        console.error('Failed to load session from cookies:', error);
        this.clearSessionCookies();
      }
    }
  }

  private clearSessionCookies(): void {
    document.cookie = `${this.config.cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${this.config.cookieOptions.path}`;
  }

  private emitEvent(event: SessionEvent): void {
    const listeners = this.eventListeners.get(event.type) || [];
    listeners.forEach(callback => {
      try {
        callback(event);
      } catch (error) {
        console.error('Event listener error:', error);
      }
    });
  }

  /**
   * Detect SSO tokens and extract user data
   */
  private async detectSSOTokens(): Promise<{ userData: SessionUser; token: string } | null> {
    try {
      // Check for SSO tokens in localStorage first (most reliable)
      const peoplesToken = localStorage.getItem('peoples_token');
      if (peoplesToken && peoplesToken.trim() !== '') {
        // console.log('🔐 Found peoples_token in localStorage');
        const userData = await this.extractUserDataFromToken(peoplesToken);
        if (userData && userData.subscriber_id && userData.subscriber_id > 0) {
          return { userData, token: peoplesToken };
        }
      }

      // Check for SSO tokens in cookies
      const cookieToken = this.getCookieValue('peoples_token');
      if (cookieToken && cookieToken.trim() !== '') {
        // console.log('🔐 Found peoples_token in cookies');
        const userData = await this.extractUserDataFromToken(cookieToken);
        if (userData && userData.subscriber_id && userData.subscriber_id > 0) {
          return { userData, token: cookieToken };
        }
      }

      // Check for other SSO-related cookies
      const ssoCookies = ['sso_token', 'auth_token', 'access_token', 'jwt_token'];
      for (const cookieName of ssoCookies) {
        const token = this.getCookieValue(cookieName);
        if (token && token.trim() !== '') {
          // console.log(`🔐 Found ${cookieName} in cookies`);
          const userData = await this.extractUserDataFromToken(token);
          if (userData && userData.subscriber_id && userData.subscriber_id > 0) {
            return { userData, token };
          }
        }
      }

      // Check for stored user IDs as fallback (only if we have a valid token)
      const storedUserId = this.getStoredUserId();
      const fallbackToken = localStorage.getItem('peoples_token');
      if (storedUserId && fallbackToken && fallbackToken.trim() !== '') {
        // console.log('🔐 Found stored user ID with valid token, creating session with fallback data');
        const fallbackUserData = this.createFallbackUserData(storedUserId);
        return { userData: fallbackUserData, token: fallbackToken };
      }

      // console.log('🔐 No valid SSO tokens found');
      return null;
    } catch (error) {
      console.error('SSO token detection failed:', error);
      return null;
    }
  }

  /**
   * Extract user data from SSO token
   */
  private async extractUserDataFromToken(token: string): Promise<SessionUser | null> {
    try {
      // Try to decode JWT token
      if (this.isJWTToken(token)) {
        const decoded = this.decodeJWT(token);
        if (decoded && decoded.subscriber_id) {
          return this.createUserDataFromDecodedToken(decoded, token);
        }
      }

      // Try to parse as JSON (if it's a stored user object)
      try {
        const parsed = JSON.parse(token);
        if (parsed && parsed.subscriber_id) {
          return { ...parsed, accessToken: token, token };
        }
      } catch (error) {
        console.error('Token extraction failed:', error);
        // Not JSON, continue with other methods
      }

      // Try to make API call to validate token and get user data
      const userData = await this.validateTokenWithAPI();
      if (userData && userData.subscriber_id) {
        return { ...userData, accessToken: token, token };
      }

      return null;
    } catch (error) {
      console.error('Token extraction failed:', error);
      return null;
    }
  }

  /**
   * Check if token is JWT format
   */
  private isJWTToken(token: string): boolean {
    return token.split('.').length === 3;
  }

  /**
   * Decode JWT token
   */
  private decodeJWT(token: string): DecodedJWT | null {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('JWT decode failed:', error);
      return null;
    }
  }

  /**
   * Create user data from decoded JWT token
   */
  private createUserDataFromDecodedToken(decoded: DecodedJWT, token: string): SessionUser {   
    return {
      accessToken: token,
      user_id: decoded.user_id || decoded.uuid || decoded.user_uuid, // Unique UUID for the user
      subscriber_id: decoded.subscriber_id || decoded.user_id || decoded.id || 0, // Login ID
      user_email: decoded.email || decoded.user_email || '',
      user_mobile: decoded.mobile || decoded.phone || '',
      first_name: decoded.first_name || decoded.given_name || '',
      last_name: decoded.last_name || decoded.family_name || '',
      company_name: decoded.company_name || decoded.company || '',
      designation: decoded.designation || decoded.title || '',
      country_name: decoded.country_name || decoded.country || '',
      is_subscribe_news_letter: decoded.is_subscribe_news_letter || false,
      is_accept_terms: decoded.is_accept_terms || false,
      is_user_type: decoded.is_user_type || '',
      userData: decoded as Record<string, unknown>,
      token,
    };
  }

  /**
   * Validate token with API and get user data
   */
  private async validateTokenWithAPI(): Promise<SessionUser | null> {
    try {
      // This would typically make an API call to validate the token
      // For now, we'll return null to indicate no API validation
      // In a real implementation, you would call your SSO validation endpoint
      // console.log('🔐 Token validation with API not implemented, skipping');
      return null;
    } catch (error) {
      console.error('API token validation failed:', error);
      return null;
    }
  }

  /**
   * Get stored user ID as fallback
   */
  private getStoredUserId(): number | null {
    try {
      // Check localStorage for stored user ID
      const storedUserId = localStorage.getItem('pm_user_id');
      if (storedUserId) {
        return parseInt(storedUserId, 10);
      }

      // Check sessionStorage for stored user ID
      const sessionUserId = sessionStorage.getItem('pm_user_id');
      if (sessionUserId) {
        return parseInt(sessionUserId, 10);
      }

      // Check cookies for stored user ID
      const cookieUserId = this.getCookieValue('pm_user_id');
      if (cookieUserId) {
        return parseInt(cookieUserId, 10);
      }

      return null;
    } catch (error) {
      console.error('Failed to get stored user ID:', error);
      return null;
    }
  }

  /**
   * Create fallback user data from stored user ID
   */
  private createFallbackUserData(subscriber_id: number): SessionUser {
    return {
      accessToken: 'fallback_token',
      user_id: this.generateUserId(), // Generate new user_id for fallback
      subscriber_id: subscriber_id,
      user_email: '',
      user_mobile: '',
      first_name: '',
      last_name: '',
      company_name: '',
      designation: '',
      country_name: '',
      is_subscribe_news_letter: false,
      is_accept_terms: false,
      is_user_type: '',
      userData: { subscriber_id: subscriber_id },
      token: 'fallback_token',
    };
  }

  /**
   * Get cookie value by name
   */
  private getCookieValue(name: string): string | null {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || null;
    }
    return null;
  }
}

// Create singleton instance
export const ssoSessionManager = new PeopleMattersSSOSessionManager();

// Export types and instance
export default ssoSessionManager;
